import { useState } from "react";
import { Button, Input, Typography } from "@material-tailwind/react";
import { format } from "date-fns";
import { api } from "../../../services/api/api";
import MonthSelect, { MonthSelectData } from "../date/MonthSelect";
import { openNativePicker } from "../date/openNativePicker";
import * as XLSX from "xlsx";
import { t } from "i18next";

interface TechnicianOvertimeExportProps {
  className?: string;
  technicianId?: string;
}

interface WorkingHours {
  start: string;
  end: string;
}

/**
 * TechnicianOvertimeExport: Component to download technician overtime statistics
 * @param className - Additional CSS classes
 * @param technicianId - Optional specific technician ID
 */
export default function TechnicianOvertimeExport({
  className = "",
  technicianId,
}: TechnicianOvertimeExportProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [monthData, setMonthData] = useState<MonthSelectData>(() => {
    return {
      value: format(new Date(), "yyyy-MM"),
    };
  });

  const [workingHours, setWorkingHours] = useState<WorkingHours>({
    start: "08:00",
    end: "17:00",
  });

  const downloadOvertimeStats = async () => {
    setIsLoading(true);
    try {
      // Extract month and year from selected month
      const [year, month] = monthData.value.split("-");

      // Prepare request body for POST
      const requestBody = {
        month: parseInt(month),
        year: parseInt(year),
        workingHoursStart: workingHours.start,
        workingHoursEnd: workingHours.end,
      };

      const response = await api.post(
        "admin/technician_times/export-overtime",
        requestBody
      );

      // Create data for single worksheet
      const headers = [
        "Technician Name",
        "Technician Email",
        "Amount of Overtime Hours",
      ];

      // Process the actual API response
      const worksheetData = [
        headers,
        ...(response as any[]).map((technician: any) => {
          const overtimeMinutes = technician.overtimeMinutes || 0;
          const hours = Math.floor(overtimeMinutes / 60);
          const minutes = overtimeMinutes % 60;
          const formatted = `${hours.toString().padStart(2, "0")}h${minutes
            .toString()
            .padStart(2, "0")}`;

          return [
            technician.technicianName,
            technician.technicianEmail,
            formatted,
          ];
        }),
      ];

      // Create workbook with single worksheet
      const workbook = XLSX.utils.book_new();

      // Add worksheet
      const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
      XLSX.utils.book_append_sheet(workbook, worksheet, "Overtime Statistics");

      // Generate Excel file
      const fileName = `Overtime for ${monthData.value.split("-")[1]}/${
        monthData.value.split("-")[0]
      } - Hours before ${workingHours.start} and after ${
        workingHours.end
      }.xlsx`;

      XLSX.writeFile(workbook, fileName);
    } catch (error) {
      console.error("Download error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={`flex flex-col h-full p-4 ${className}`}>
      <div className="flex-grow pb-4 space-y-4">
        {/* Working Hours Configuration */}
        <div>
          <Typography variant="h6" className="mb-2">
            {t("common:workingHours")}
          </Typography>
          <div className="flex gap-4">
            <div className="flex-1">
              <Typography variant="small" className="mb-1">
                {t("common:startTime")}
              </Typography>
              <Input
                type="time"
                value={workingHours.start}
                onClick={openNativePicker}
                onChange={(e) =>
                  setWorkingHours((prev) => ({
                    ...prev,
                    start: e.target.value,
                  }))
                }
                className="bg-white shadow-none"
                labelProps={{
                  className: "hidden",
                }}
                containerProps={{ className: "min-w-[100px]" }}
              />
            </div>
            <div className="flex-1">
              <Typography variant="small" className="mb-1">
                {t("common:endTime")}
              </Typography>
              <Input
                type="time"
                value={workingHours.end}
                onClick={openNativePicker}
                onChange={(e) =>
                  setWorkingHours((prev) => ({ ...prev, end: e.target.value }))
                }
                className="bg-white shadow-none"
                labelProps={{
                  className: "hidden",
                }}
                containerProps={{ className: "min-w-[100px]" }}
              />
            </div>
          </div>
          <Typography variant="small" className="text-gray-600 mt-2">
            {t("common:weekendOvertimeNote")}
          </Typography>
        </div>

        {/* Month Selection */}
        <div>
          <Typography variant="h6" className="mb-2">
            {t("common:period")} - {t("common:monthlyExport")}
          </Typography>
          <MonthSelect
            onChange={setMonthData}
            initialData={{
              value: format(new Date(), "yyyy-MM"),
            }}
          />
        </div>
      </div>

      <div className="footer mt-auto pt-4">
        <Button
          onClick={downloadOvertimeStats}
          disabled={isLoading}
          className="w-full flex items-center justify-center gap-2 bg-blue-500"
          color="blue"
          size="sm"
        >
          {isLoading
            ? t("common:downloading")
            : t("common:exportOvertimeStats")}
        </Button>
      </div>
    </div>
  );
}
